// @ts-strict-ignore
import React from "react";
import {
  CardBack,
  CardFront,
  CardInner,
  CloseButton,
  FlipButton,
  Wrapper
} from "./styles";
import { HubbleProvider, mediaQuery, Card as HubbleCard } from "@hubble/web";
import styled from "@emotion/styled";
import { useTheme, Global, css } from "@emotion/react";
import { useMedia } from "react-use";

interface CardSide {
  component: React.ReactNode;
  background?: string;
  isInverted?: boolean;
}

interface CardProps {
  front: CardSide;
  back?: CardSide;
  className?: string;
  hoverCard?: boolean;
}

const TIME = 1;

export const Card = ({
  front,
  back,
  className,
  hoverCard = false
}: CardProps) => {
  const [flip, setFlip] = React.useState(false);
  const { isDark } = useTheme();
  const mobile = useMedia(mediaQuery.mobileXsDown, false);
  const hoverEffect = hoverCard && !mobile;
  const toggleFlip = (e) => {
    e.type === "click" && setFlip(!flip);
    hoverEffect &&
      (e.type === "mouseleave" || e.type === "mouseenter") &&
      setFlip(!flip);
  };
  return (
    <React.Fragment>
      <Global
        styles={
          flip &&
          css`
            // prevent scrolling while full back modal is open
            @media ${mediaQuery.mobileXsDown} {
              body {
                overflow: hidden !important;
              }
            }
          `
        }
      />
      <Wrapper
        className={className}
        flipped={Boolean(flip)}
        onMouseEnter={(e) => {
          if (back && !flip) {
            toggleFlip(e);
          }
        }}
        onMouseLeave={(e) => {
          if (flip) {
            toggleFlip(e);
          }
        }}
      >
        <CardInner time={TIME} flip={flip}>
          <CardFront
            cursor={back ? "pointer" : "auto"}
            background={front.background}
            onClick={(e) => {
              if (back && !flip) {
                toggleFlip(e);
              }
            }}
          >
            {front.isInverted ? (
              <HubbleProvider scheme={isDark ? "light" : "dark"}>
                {front.component}
                {back && !hoverEffect && <FlipButton />}
              </HubbleProvider>
            ) : (
              <React.Fragment>
                {front.component}
                {back && !hoverEffect && <FlipButton />}
              </React.Fragment>
            )}
          </CardFront>
          {back &&
            (back.isInverted ? (
              <HubbleProvider scheme={isDark ? "light" : "dark"}>
                <CardBack background={back.background} flipped={Boolean(flip)}>
                  <React.Fragment>
                    {back.component}
                    {!hoverEffect && <CloseButton onClick={toggleFlip} />}
                  </React.Fragment>
                </CardBack>
              </HubbleProvider>
            ) : (
              <CardBack
                background={back.background}
                flipped={Boolean(flip)}
                className="back"
              >
                <React.Fragment>
                  {back.component}
                  {!hoverEffect && <CloseButton onClick={toggleFlip} />}
                </React.Fragment>
              </CardBack>
            ))}
        </CardInner>
      </Wrapper>
    </React.Fragment>
  );
};

const CardWithBg = styled(HubbleCard)<{
  background?: string;
  backgroundImage?: string;
}>`
  box-sizing: border-box;
  border: none;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  backface-visibility: hidden;
  position: absolute;

  ${({ background }) => background && `background: ${background};`}
`;

// A more simple version of the other Card in this file
// This is used for InstitutionsCardGrid's MobileCard, TabletCard, and DesktopCard to fix the issue of Hubspot Modal being contained within the card.
// Decided not to refactor in case it disrupts the styling of other components that imports Card, and also since we are migrating to Builder instead of contentful.
export const SimpleCard = ({ front }: CardProps) => {
  const { isDark } = useTheme();

  return (
    <HubbleProvider scheme={isDark ? "light" : "dark"}>
      <CardWithBg background={front.background}>{front.component}</CardWithBg>
    </HubbleProvider>
  );
};
